import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RatingBadge } from "@/components/RatingBadge";
import { tradeLabel } from "@/components/TradeCategoryPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [trades, setTrades] = useState([]);
  const [quoteForm, setQuoteForm] = useState({ amount: "", eta_days: "", message: "" });
  const [assignForm, setAssignForm] = useState({ assignee_id: "", scheduled_at: "" });
  const [review, setReview] = useState({ rating: 5, comment: "" });
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [rescheduleDate, setRescheduleDate] = useState("");

  const reload = async () => {
    const { data } = await api.get(`/jobs/${id}`);
    setJob(data);
    try {
      const q = await api.get(`/jobs/${id}/quotes`);
      setQuotes(q.data);
    } catch { setQuotes([]); }
    try {
      const m = await api.get(`/jobs/${id}/messages`);
      setMessages(m.data);
    } catch { setMessages([]); }
    if (user?.role === "admin") {
      const t = await api.get(`/trades${data.category ? `?category=${data.category}` : ""}`);
      setTrades(t.data);
    }
  };

  useEffect(() => { if (user) reload(); /* eslint-disable-next-line */ }, [id, user]);

  // Stripe success redirect → confirm
  const [search] = useSearchParams();
  useEffect(() => {
    const paid = search.get("paid");
    const sid = search.get("session_id");
    if (paid && paid !== "cancelled" && sid) {
      api.post(`/payments/confirm/${sid}`).then(r => {
        if (r.data.paid) toast.success("Payment received");
        reload();
      }).catch(()=>{});
    } else if (paid === "cancelled") {
      toast.error("Payment cancelled");
    }
    // eslint-disable-next-line
  }, [search.toString()]);

  const payNow = async (kind) => {
    try {
      const { data } = await api.post(`/jobs/${id}/checkout/${kind}`);
      // Stripe appends session_id via the success_url template on return
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/jobs/${id}/photos`, { data_url: reader.result });
        toast.success("Photo added");
        reload();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };

  if (!job) return <div data-testid="job-loading" className="text-center py-24 text-[#78716C]">Loading…</div>;

  const updateStatus = async (status) => {
    try {
      await api.patch(`/jobs/${id}/status`, { status });
      toast.success(`Status: ${status.replace("_"," ")}`);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitQuote = async () => {
    try {
      await api.post(`/jobs/${id}/quotes`, { amount: parseFloat(quoteForm.amount), eta_days: parseInt(quoteForm.eta_days), message: quoteForm.message });
      toast.success("Quote submitted");
      setQuoteForm({ amount:"", eta_days:"", message:"" });
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const acceptQuote = async (quoteId) => {
    try {
      await api.post(`/quotes/${quoteId}/accept`);
      toast.success("Quote accepted. Job is now scheduled.");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const assignJob = async () => {
    const trade = trades.find(t => t.id === assignForm.assignee_id);
    try {
      await api.post(`/jobs/${id}/assign`, {
        assignee_id: assignForm.assignee_id,
        assignee_type: trade ? "trade" : "staff",
        scheduled_at: assignForm.scheduled_at || null,
      });
      toast.success("Assigned");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitReview = async () => {
    try {
      await api.post(`/jobs/${id}/review`, review);
      toast.success("Thank you for your review.");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    try {
      await api.post(`/jobs/${id}/messages`, { body: msgText });
      setMsgText("");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doReschedule = async () => {
    if (!rescheduleDate) return;
    try {
      await api.patch(`/jobs/${id}/reschedule`, { preferred_date: rescheduleDate });
      toast.success("New date requested. Olivia will confirm.");
      setRescheduleDate("");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const canQuote = user?.role === "trade" && job.status === "requested" && (user.trade_categories?.length ? user.trade_categories.includes(job.category) : job.category === user.trade_category);
  const isCustomer = user?.role === "customer" && job.customer_id === user.id;
  const isAdmin = user?.role === "admin";
  const isAssignee = job.assignee_id === user?.id;

  return (
    <div data-testid="job-detail-page" className="max-w-5xl mx-auto px-6 md:px-8 py-12">
      <button onClick={() => navigate(-1)} className="text-sm od-link">← Back</button>
      <div className="flex flex-wrap justify-between items-start mt-4 gap-4">
        <div>
          <span className="od-label">{job.category} · {job.urgency}</span>
          <h1 className="font-display text-4xl md:text-5xl mt-2">{job.title}</h1>
        </div>
        <StatusBadge status={job.status} />
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-10">
        <div className="md:col-span-2 od-card p-6">
          <p className="od-label">Brief</p>
          <p className="mt-3 leading-relaxed">{job.description}</p>
          <div className="grid grid-cols-2 gap-6 mt-8 text-sm">
            <div><p className="od-label">Customer</p><p className="mt-1">{job.customer_name}</p></div>
            <div><p className="od-label">Assignee</p><p className="mt-1 flex items-center gap-2">{job.assignee_name || "Unassigned"}{job.assignee_type === "trade" && job.assignee_name && <RatingBadge rating={job.assignee_rating} count={job.assignee_review_count} testid="job-assignee-rating" />}</p></div>
            <div><p className="od-label">Address</p><p className="mt-1">{job.address}</p></div>
            <div><p className="od-label">Preferred</p><p className="mt-1">{job.preferred_date || "—"}</p></div>
            <div><p className="od-label">Price</p><p className="mt-1">{job.price ? `£${job.price}` : "Pending"}</p></div>
            <div><p className="od-label">Type</p><p className="mt-1 capitalize">{job.job_type}</p></div>
          </div>
        </div>

        <div className="od-card p-6">
          <p className="od-label">Actions</p>
          {(isAssignee || isAdmin) && (
            <div className="space-y-2 mt-4">
              {job.status === "scheduled" && <Button data-testid="action-start" onClick={()=>updateStatus("in_progress")} className="w-full rounded-full bg-[#2A2624] hover:bg-[#1E1A18]">Start job</Button>}
              {job.status === "in_progress" && <Button data-testid="action-complete" onClick={()=>updateStatus("completed")} className="w-full rounded-full bg-[#2A2624] hover:bg-[#2A2624]">Mark complete</Button>}
            </div>
          )}
          {isCustomer && job.status !== "completed" && job.status !== "cancelled" && (
            <Button data-testid="action-cancel" onClick={()=>updateStatus("cancelled")} variant="outline" className="w-full rounded-full mt-4 border-[#B04A4A] text-[#B04A4A]">Cancel job</Button>
          )}
          {isCustomer && !["completed","cancelled"].includes(job.status) && (
            <div className="mt-4">
              <p className="od-label">Reschedule</p>
              <div className="flex gap-2 mt-2">
                <Input data-testid="reschedule-date" type="date" value={rescheduleDate} onChange={e=>setRescheduleDate(e.target.value)} className="rounded-xl border-[#E2DBD6]" />
                <Button data-testid="reschedule-submit" onClick={doReschedule} disabled={!rescheduleDate} variant="outline" className="rounded-full">Request</Button>
              </div>
            </div>
          )}
          {isCustomer && job.price && job.status === "scheduled" && !job.deposit_paid && (
            <Button data-testid="pay-now" onClick={()=>payNow("deposit")} className="w-full rounded-full bg-[#C77F8C] hover:bg-[#A86A55] mt-4 text-white">
              Pay now · £{job.price.toFixed(2)}
            </Button>
          )}
          {isCustomer && job.deposit_paid && (
            <div data-testid="payment-paid-badge" className="mt-3 text-xs text-[#2A2624] font-semibold">✓ Paid in full · job settled</div>
          )}
        </div>
      </div>

      {/* Photos */}
      <div className="mt-10">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl">Photos</h2>
          {(isCustomer || isAssignee || isAdmin) && (
            <label data-testid="photo-upload-label" className="cursor-pointer text-sm od-link">
              + Add photo
              <input data-testid="photo-input" type="file" accept="image/*" className="hidden" onChange={(e)=>uploadPhoto(e.target.files?.[0])} />
            </label>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          {(job.photos || []).map(p => (
            <div key={p.id || p} data-testid={`photo-${p.id || 'x'}`} className="aspect-square rounded-xl overflow-hidden bg-[#E2DBD6]">
              <img src={typeof p === "string" ? p : p.data_url} alt="job" className="w-full h-full object-cover" />
            </div>
          ))}
          {(!job.photos || job.photos.length === 0) && <p className="text-sm text-[#78716C] col-span-full">No photos yet.</p>}
        </div>
      </div>

      {/* Quotes section */}
      {(job.job_type === "quote" || quotes.length > 0) && (
        <div className="mt-10">
          <h2 className="font-display text-2xl">Quotes</h2>
          {quotes.length === 0 && <p className="text-sm text-[#78716C] mt-2">No quotes yet.</p>}
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {quotes.map(q => (
              <div data-testid={`quote-${q.id}`} key={q.id} className="od-card p-5">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-display text-xl">£{q.amount}</p>
                    <p className="text-xs text-[#78716C]">{q.trade_name} · ETA {q.eta_days} days</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${q.status === "accepted" ? "bg-[#2A2624]/15 text-[#2A2624]" : q.status === "declined" ? "bg-[#B04A4A]/15 text-[#B04A4A]" : "bg-[#E2DBD6]"}`}>{q.status}</span>
                </div>
                {q.message && <p className="text-sm text-[#78716C] mt-3">{q.message}</p>}
                {isCustomer && q.status === "open" && job.status !== "scheduled" && (
                  <Button data-testid={`accept-quote-${q.id}`} onClick={()=>acceptQuote(q.id)} className="mt-4 rounded-full bg-[#2A2624] hover:bg-[#1E1A18]">Accept this quote</Button>
                )}
              </div>
            ))}
          </div>

          {canQuote && (
            <div className="od-card p-6 mt-6">
              <h3 className="font-display text-xl">Submit a quote</h3>
              <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div><Label className="od-label">Amount (£)</Label><Input data-testid="quote-amount" type="number" value={quoteForm.amount} onChange={e=>setQuoteForm({...quoteForm, amount:e.target.value})} className="mt-2 rounded-xl border-[#E2DBD6]" /></div>
                <div><Label className="od-label">ETA (days)</Label><Input data-testid="quote-eta" type="number" value={quoteForm.eta_days} onChange={e=>setQuoteForm({...quoteForm, eta_days:e.target.value})} className="mt-2 rounded-xl border-[#E2DBD6]" /></div>
                <div><Label className="od-label">Note</Label><Input data-testid="quote-note" value={quoteForm.message} onChange={e=>setQuoteForm({...quoteForm, message:e.target.value})} className="mt-2 rounded-xl border-[#E2DBD6]" /></div>
              </div>
              <Button data-testid="quote-submit" onClick={submitQuote} disabled={!quoteForm.amount || !quoteForm.eta_days} className="mt-4 rounded-full bg-[#2A2624] hover:bg-[#1E1A18]">Send quote</Button>
            </div>
          )}
        </div>
      )}

      {/* Admin assign */}
      {isAdmin && !job.assignee_id && (
        <div className="od-card p-6 mt-10">
          <h3 className="font-display text-xl">Assign trade</h3>
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            <div className="md:col-span-2">
              <Label className="od-label">Trade ({job.category})</Label>
              <Select value={assignForm.assignee_id} onValueChange={v=>setAssignForm({...assignForm, assignee_id:v})}>
                <SelectTrigger data-testid="assign-trade" className="mt-2 rounded-xl border-[#E2DBD6]"><SelectValue placeholder="Select trade" /></SelectTrigger>
                <SelectContent>
                  {trades.map(t => <SelectItem key={t.id} value={t.id}>{t.name} · {tradeLabel(t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="od-label">Scheduled date</Label>
              <Input data-testid="assign-date" type="date" value={assignForm.scheduled_at} onChange={e=>setAssignForm({...assignForm, scheduled_at:e.target.value})} className="mt-2 rounded-xl border-[#E2DBD6]" />
            </div>
          </div>
          <Button data-testid="assign-submit" disabled={!assignForm.assignee_id} onClick={assignJob} className="mt-4 rounded-full bg-[#2A2624] hover:bg-[#1E1A18]">Confirm assignment</Button>
        </div>
      )}

      {/* Messages */}
      {(isCustomer || isAssignee || isAdmin || user?.role === "staff") && (
        <div className="mt-10" data-testid="messages-section">
          <h2 className="font-display text-2xl">Messages</h2>
          <div className="od-card p-6 mt-4">
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {messages.length === 0 && <p className="text-sm text-[#78716C]">No messages yet. Say hello 👋</p>}
              {messages.map(m => {
                const mine = m.sender_id === user?.id;
                return (
                  <div key={m.id} data-testid={`message-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#2A2624] text-white" : "bg-[#F4EFEA] text-[#2A2624]"}`}>
                      {!mine && <p className="text-[10px] uppercase tracking-widest opacity-70 mb-1">{m.sender_name} · {m.sender_role}</p>}
                      <p className="text-sm leading-relaxed">{m.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 mt-4">
              <Input data-testid="message-input" value={msgText} onChange={e=>setMsgText(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")sendMessage();}} placeholder="Write a message…" className="rounded-xl border-[#E2DBD6]" />
              <Button data-testid="message-send" onClick={sendMessage} disabled={!msgText.trim()} className="rounded-full bg-[#2A2624] hover:bg-[#1E1A18]">Send</Button>
            </div>
          </div>
        </div>
      )}

      {/* Review */}
      {isCustomer && job.status === "completed" && (
        job.has_review ? (
          <div className="od-card p-6 mt-10" data-testid="review-thanks">
            <h3 className="font-display text-xl">Thanks for your review ★</h3>
            <p className="text-sm text-[#78716C] mt-2">Your feedback helps {job.assignee_name || "your partner"} and other families choosing Olivia Does.</p>
          </div>
        ) : (
        <div className="od-card p-6 mt-10">
          <h3 className="font-display text-xl">Leave a review</h3>
          <div className="flex gap-2 mt-3">
            {[1,2,3,4,5].map(n => (
              <button key={n} data-testid={`star-${n}`} onClick={()=>setReview({...review, rating:n})} className={`w-10 h-10 rounded-full ${review.rating>=n ? "bg-[#C77F8C] text-white" : "bg-[#E2DBD6] text-[#78716C]"}`}>★</button>
            ))}
          </div>
          <Textarea data-testid="review-text" rows={3} value={review.comment} onChange={e=>setReview({...review, comment:e.target.value})} placeholder="How was your experience?" className="mt-4 rounded-xl border-[#E2DBD6]" />
          <Button data-testid="review-submit" onClick={submitReview} className="mt-4 rounded-full bg-[#2A2624] hover:bg-[#1E1A18]">Submit review</Button>
        </div>
        )
      )}
    </div>
  );
}
